<template>
  <div class="full-page-width relative locations">
    <div class="row">
      <div class="col-xs-4 text-box">
        <InfoTextBox
          :title="$t('consolidations')"
          :text="$t('consolidations-info-text')"
          :btnText="$t('create-consolidation')"
          @action="createConsolidation" />
      </div>
      <div class="col-xs-8">
        <!-- Initial skeleton: shown when loading first page and no items yet -->
        <div v-if="loading && get_consolidations_list.items.length === 0" class="list-table">
          <div class="table">
            <div class="table-body">
              <div
                class="table-row-holder"
                v-for="i in skeletonLoadingList"
                :key="`skeleton-${i}`">
                <TableRowSkeleton
                  :checkbox="false"
                  :type="true"
                  :data="skeletonReferenceData"/>
              </div>
            </div>
          </div>
        </div>

        <!-- Actual content when loaded -->
        <ul class="list-table" v-else-if="get_consolidations_list.items.length > 0">
          <li
            v-for="(item, index) in get_consolidations_list.items"
            :key="`item-${index}`"
            @click="goToPage(item.consolidation_number)"
            class="list-table-item"
          >
            <div class="table-cell small">
              <div class="status-logo-box float-left">
                <LogosSVG
                  :name="item.shipping_agent_name"
                  size="45" />
              </div>
            </div>
            <div class="table-cell">
              <p>
                <span class="main-info">
                  {{ formatDate(item.consolidation_date) }} - {{ `#${item.consolidation_number}` }}
                </span>
                <span class="additional-info" :style="{ color: color(item.consolidation_status) }">
                  {{ $t(getStatus(item.consolidation_status)) }} {{ `(${item.consolidated_shipments} ${$t('lower_case_shipments')})` }}
                </span>
              </p>
            </div>
          </li>
        </ul>

        <!-- Empty state -->
        <div class="table" v-else-if="!loading">
          <div class="table-body">
            <div class="table-row-holder" style="text-align: center; padding: 20px;">
              {{ $t('No results') }}
            </div>
          </div>
        </div>

        <!-- Infinite scroll sentinel -->
        <div v-if="hasMore && !loading && !loadingMore" v-observe-visibility="visibilityOptions"></div>

        <!-- Load-more skeleton: appended below items while fetching next page -->
        <div v-if="loadingMore" class="list-table">
          <div class="table">
            <div class="table-body">
              <div
                class="table-row-holder"
                v-for="i in skeletonLoadingList"
                :key="`more-${i}`">
                <TableRowSkeleton
                  :checkbox="false"
                  :type="true"
                  :data="skeletonReferenceData"/>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <Popup
      v-if="openPopup"
      :popupMedium=true
      :isPopupSubmitDisabled="isPopupSubmitDisabled"
      minHeight="500px"
      :confirmText="$t('generate')"
      @cancel="togglePopup"
      @confirm="submitForm">
      <template v-slot:header>
        <h2>{{ $t('create_consolidation') }}</h2>
      </template>
      <div>
        <div class="col-xs-12">
          <InputSelect
            class="m-b-20"
            :multiSelect=true
            :label="$t('accounts')"
            @change="selectSubaccount"
            :preselected="form.subaccounts"
            :options="get_allowed_subaccounts_select_options"
            :rules="{required: true}" />
        </div>
        <div class="col-xs-6 m-b-20">
          <Calendar
            :disable-weekend="false"
            :noMinDate="true"
            :noDate="true"
            :label="$t('from_date')"
            v-model="form.from_date"
            @input="resetDynamicData"
            :rules="{required: false}" />
        </div>
        <div class="col-xs-6 m-b-20">
          <Calendar
            :disable-weekend="false"
            :label="$t('to_date')"
            v-model="form.to_date"
            :date="form.to_date"
            :min-date-prop="form.from_date"
            @input="resetDynamicData"
            :rules="{required: true}" />
        </div>
        <div class="col-xs-12">
          <InputSelect
            class="m-b-20"
            :multiSelect=false
            :label="$t('shipping_agent_name')"
            v-model="form.shipping_agent_id"
            @change="getCarrierAccountOptions"
            :options="getCarrierOptions"
            :rules="{required: true}" />
        </div>
        <div class="col-xs-12" v-if="get_is_ship365">
          <InputSelect
            class="m-b-20"
            v-if="form.shipping_agent_id"
            :multiSelect=false
            :label="$t('agreement')"
            v-model="form.account_id"
            @change="resetDynamicData"
            :options="carrierAccountOptions"
            :rules="{required: true}" />
        </div>
        <div class="col-xs-12">
          <InputSelect
            v-if="form.shipping_agent_id"
            class="m-b-20"
            :multiSelect=false
            :label="$t('type')"
            v-model="form.type"
            @change="getAllowedConsolidations"
            :options="getTypeOptions"
            :rules="{required: true}" />
        </div>
        <div class="col-xs-12">
          <InputSelect
            v-if="form.type"
            class="m-b-20"
            :multiSelect=false
            :label="$t('add_shipments_to')"
            v-model="form.consolidation_number"
            :options="consolidationOptions"
            :rules="{required: true}" />
        </div>
      </div>
    </Popup>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from 'vuex';
import AddressPicker from "@/components/global/addressPicker/index.vue";
import PhoneSelect from "@/components/elements/inputs/phone.vue";
import Btn from '@/components/elements/buttons';
import InputText from '@/components/elements/inputs/text.vue';
import Popup from '@/components/global/popup';
import InputSelect from '@/components/elements/inputs/select.vue';
import Toggle from '@/components/elements/inputs/toggle.vue';
import LogosSVG from "@/assets/images/logosSVG";
import date from '@/mixins/date'
import Calendar from '@/components/elements/inputs/calendar.vue';
import InfoTextBox from '@/components/global/infoTextBox';
import TableRowSkeleton from '@/components/table/tableRowSkeleton.vue'; // Add this import
import formValidator from '@/mixins/formValidator';

export default {
  name: 'Consolidations',
  components: {
    Btn,
    AddressPicker,
    PhoneSelect,
    Popup,
    InputText,
    InputSelect,
    Toggle,
    InfoTextBox,
    LogosSVG,
    Calendar,
    TableRowSkeleton, // Add this component
  },
  mixins: [date, formValidator],
  data() {
    return {
      page: 1,
      loading: false, // Add loading state
      loadingMore: false,
      openPopup: false,
      carrierAccountOptions: [],
      consolidationOptions: [],
      form: {
        from_date: '',
        to_date: new Date(),
        shipping_agent_id: '',
        type: '',
        consolidation_number: '',
        subaccounts: [],
        account_id: '',
        consolidation_status: ''
      }
    };
  },
  computed: {
    ...mapGetters([
      'get_is_ship365',
      'get_consolidations_list',
      'get_allowed_subaccounts_select_options'
    ]),
    // Add skeleton loading configuration
    skeletonLoadingList() {
      return Array.from({ length: 5 }, (_, i) => i + 1); // Show 5 skeleton rows
    },
    skeletonReferenceData() {
      return [
        { field: 'logo' },
        { field: 'info' }
      ];
    },
    hasMore() {
      return this.get_consolidations_list.count > this.get_consolidations_list.items.length;
    },
    visibilityOptions() {
      return {
        callback: this.visibilityChanged,
        intersection: {
          threshold: 0.1,
        },
      };
    },
    getCarrierOptions() {
      return [
        {value: 1, text: 'PostNord'},
        {value: 3, text: 'Bring'}
      ];
    },
    getTypeOptions() {
      return [
        {value: 1, text: this.$t('parcel')},
        {value: 2, text: this.$t('letter_tracked')},
        {value: 3, text: this.$t('letter_untracked')}
      ];
    },
    isPopupSubmitDisabled() {
      if (this.form.subaccounts.length < 1) return true;
      if (this.form.consolidation_number === '') return true;

      let formWithValidators = [
        {
          formFeild:'from_date',
          value: this.form.from_date,
          validators:{required: false}
        },
        {
          formFeild:'to_date',
          value: this.form.to_date,
          validators:{required: true}
        },
        {
          formFeild:'shipping_agent_id',
          value: this.form.shipping_agent_id,
          validators:{required: true}
        },
        {
          formFeild:'type',
          value: this.form.type,
          validators:{required: true}
        },
      ];

      if (this.get_is_ship365) {
        formWithValidators.push(
          {
            formFeild:'account_id',
            value: this.form.account_id,
            validators:{required: true}
          }
        )
      }

      return this.validateInput(formWithValidators);
    },
  },
  created() {
    this.getConsolidations();
    this.setInitialSelectedSubaccounts()
  },
  methods: {
    resetDynamicData() {
      this.form.type = '';
      this.form.consolidation_number = '';
    },
    selectSubaccount(val) {
      this.form.subaccounts = val;
      this.resetDynamicData();
    },
    setInitialSelectedSubaccounts() {
      this.form.subaccounts = this.get_allowed_subaccounts_select_options.map((obj) => {
        return obj.value;
      });
    },
    createConsolidation() {
      this.togglePopup()
    },
    togglePopup() {
      this.openPopup = !this.openPopup;
      this.form.from_date = '';
      this.form.to_date = '';
    },
    submitForm() {
      this.loading = true; // Show loading when creating consolidation
      //If consolidation number is 0 create new consolidation
      this.$store.dispatch('CREATE_CONSOLIDATION', this.form).then((res) => {
        this.togglePopup();
        this.page = 1;
        this.getConsolidations();
      }).finally(() => {
        this.loading = false; // Hide loading when done
      });
    },
    resetForm() {
      this.form = {
        from_date: new Date(),
        to_date: new Date(),
        shipping_agent_id: '',
        type: '',
        consolidation_number: '',
        subaccounts: [],
        account_id: ''
      }
    },
    getConsolidations(isLoadMore = false) {
      if (isLoadMore) {
        this.loadingMore = true;
      } else {
        this.loading = true;
      }
      this.$store.dispatch('GET_CONSOLIDATIONS', this.page).finally(() => {
        this.loading = false;
        this.loadingMore = false;
      });
    },
    getCarrierAccountOptions(val) {
      this.form.shipping_agent_id = val;
      //We need to reset those data on change
      this.form.account_id = '';
      this.form.consolidation_number = '';
      this.$store.dispatch('GET_CONSOLIDATION_CARRIER_ACCOUNTS', {shipping_agent_id: val}).then((res) => {
        this.carrierAccountOptions = [
          ...res.map((obj) => {
            return {
              value: obj.id,
              text: obj.account_name
            }
          })
        ];
      })
    },
    getAllowedConsolidations(val) {
      this.form.type = val;
      this.form.consolidation_number = '';
      this.$store.dispatch('GET_AVAILABLE_CONSOLIDATIONS', this.form).then((res) => {
        this.consolidationOptions = [
          {value: 0, text: this.$t('new_consolidation')},
          ...res.map((obj) => {
            return {
              value: obj.consolidation_number,
              text:
                `Consolidation #${obj.consolidation_number} (${obj.consolidation_date})`
            };
          }),
        ];
      })
    },
    goToPage(pageID) {
      this.$router.push(`consolidations/${pageID}`);
    },
    getStatus(statusId) {
      if (statusId == 1) {
        return 'draft';
      } else if (statusId == 2) {
        return 'ready';
      } else if (statusId == 3) {
        return 'completed';
      }
    },
    color(status) {
      if (status == 1) {
        return '';
      } else if (status == 2) {
        return '#FFCB52';
      } else if (status == 3) {
        return '#60D27C';
      }
    },
    visibilityChanged(isVisible) {
      if (!isVisible || this.loadingMore || this.loading || !this.hasMore) return;
      this.loadingMore = true;
      this.page++;
      this.getConsolidations(true);
    },
  },
};
</script>
